<template>
    <v-container fluid>
        <v-card outlined class="mx-auto" max-width="1000">
            <v-card-title class="title font-weight-bold primary white--text py-4 px-6 d-flex align-center">
                <v-icon left dark>mdi-local-mall</v-icon>
                {{ currentItem.ID ? $t('tmdt_form.title_detail') : $t('tmdt_form.title_create') }}
                <v-spacer></v-spacer>
                <v-chip dark class="font-weight-bold" :color="display_color(currentItem.Status, product_status_list)">
                    {{ currentItem.Status | display_value(product_status_list) }}
                </v-chip>
            </v-card-title>

            <v-card-text class="pa-6">
                <v-form ref="edit_form" lazy-validation>
                    <v-row>
                        <!-- Basic Information -->
                        <v-col cols="12" md="6">
                            <v-card outlined class="mb-4 fill-height">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-information-outline</v-icon>
                                    {{ $t('tmdt_form.general_info') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12">
                                            <v-text-field
                                                outlined
                                                dense
                                                :label="$t('tmdt_form.tracking_code') + ' *'"
                                                v-model="currentItem.TrackingCode"
                                                :rules="[v => !!v || $t('tmdt_form.tracking_code_required')]"
                                                prepend-inner-icon="mdi-barcode"
                                                clearable
                                            ></v-text-field>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.UID"
                                                :items="customers"
                                                item-value="ID"
                                                item-text="Username"
                                                :label="$t('tmdt_form.customer') + ' *'"
                                                :rules="[v => !!v || $t('tmdt_form.customer_required')]"
                                                prepend-inner-icon="mdi-account"
                                            ></v-autocomplete>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-autocomplete
                                                outlined
                                                dense
                                                v-model="currentItem.StoreID"
                                                :items="storages"
                                                item-value="ID"
                                                item-text="Name"
                                                :label="$t('tmdt_form.storage') + ' *'"
                                                :rules="[v => !!v || $t('tmdt_form.storage_required')]"
                                                prepend-inner-icon="mdi-store"
                                            ></v-autocomplete>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-select
                                                outlined
                                                dense
                                                v-model="currentItem.Status"
                                                :items="product_status_list"
                                                item-value="value"
                                                :item-text="p => p.i18n ? $t(p.i18n) : p.name"
                                                :label="$t('tmdt_form.status') + ' *'"
                                                :rules="[v => !!v || $t('tmdt_form.status_required')]"
                                                prepend-inner-icon="mdi-circle-slice-8"
                                            ></v-select>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('tmdt_form.number_of_pack') + ' *'"
                                                v-model="currentItem.NumberOfPack"
                                                :rules="[v => !!v || $t('tmdt_form.number_of_pack_required')]"
                                                :decimal="0"
                                            ></h-currency-input>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-textarea
                                                outlined
                                                dense
                                                :label="$t('tmdt_form.note')"
                                                v-model="currentItem.Note"
                                                prepend-inner-icon="mdi-note-text"
                                                rows="3"
                                            ></v-textarea>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <!-- Package Specs -->
                        <v-col cols="12" md="6">
                            <v-card outlined class="mb-4">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-cube-outline</v-icon>
                                    {{ $t('tmdt_form.specs') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('tmdt_form.length')"
                                                v-model="length"
                                                :decimal="1"
                                                @input="calcVolume"
                                            ></h-currency-input>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('tmdt_form.width')"
                                                v-model="width"
                                                :decimal="1"
                                                @input="calcVolume"
                                            ></h-currency-input>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('tmdt_form.height')"
                                                v-model="height"
                                                :decimal="1"
                                                @input="calcVolume"
                                            ></h-currency-input>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <h-currency-input
                                                :label="$t('tmdt_form.weight') + ' *'"
                                                v-model="currentItem.Weight"
                                                :rules="[v => !!v || $t('tmdt_form.weight_required')]"
                                                :decimal="1"
                                            ></h-currency-input>
                                        </v-col>

                                        <v-col cols="12">
                                            <h-currency-input
                                                :label="$t('tmdt_form.volume')"
                                                v-model="currentItem.Volume"
                                                :decimal="3"
                                                disabled
                                            ></h-currency-input>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>

                            <!-- Links & Timeline -->
                            <v-card outlined class="mb-4" v-if="currentItem.ID">
                                <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-3">
                                    <v-icon left color="primary">mdi-link-variant</v-icon>
                                    {{ $t('tmdt_form.link_timeline') }}
                                </v-card-title>
                                <v-card-text class="pt-4">
                                    <v-row>
                                        <v-col cols="12" sm="6" v-if="currentItem.BigPackageCode">
                                            <div class="grey--text caption">{{ $t('tmdt_form.big_package_code') }}</div>
                                            <div class="subtitle-2 font-weight-bold primary--text">
                                                {{ currentItem.BigPackageCode }}
                                            </div>
                                        </v-col>

                                        <v-col cols="12" sm="6" v-if="currentItem.OrderCodes">
                                            <div class="grey--text caption">{{ $t('tmdt_form.truck_code') }}</div>
                                            <div class="subtitle-2 font-weight-bold success--text" v-html="currentItem.OrderCodes">
                                            </div>
                                        </v-col>

                                        <v-col cols="12">
                                            <v-divider class="mb-3"></v-divider>
                                            <div class="grey--text caption mb-2">{{ $t('tmdt_form.timeline') }}</div>
                                            
                                            <div class="d-flex flex-column gap-2">
                                                <div class="d-flex justify-space-between py-1" v-if="currentItem.CreatedDate">
                                                    <span>{{ $t('tmdt_form.date_created') }}:</span>
                                                    <span class="font-weight-bold">{{ currentItem.CreatedDate | display_date('DD-MM-YYYY HH:mm') }}</span>
                                                </div>
                                                <div class="d-flex justify-space-between py-1" v-if="currentItem.StoreCNDate">
                                                    <span>{{ $t('tmdt_form.date_store_cn') }}:</span>
                                                    <span class="font-weight-bold">{{ currentItem.StoreCNDate | display_date('DD-MM-YYYY HH:mm') }}</span>
                                                </div>
                                                <div class="d-flex justify-space-between py-1" v-if="currentItem.StoreCNExpDate">
                                                    <span>{{ $t('tmdt_form.date_store_cn_exp') }}:</span>
                                                    <span class="font-weight-bold">{{ currentItem.StoreCNExpDate | display_date('DD-MM-YYYY HH:mm') }}</span>
                                                </div>
                                                <div class="d-flex justify-space-between py-1" v-if="currentItem.StoreVNDate">
                                                    <span>{{ $t('tmdt_form.date_store_vn') }}:</span>
                                                    <span class="font-weight-bold">{{ currentItem.StoreVNDate | display_date('DD-MM-YYYY HH:mm') }}</span>
                                                </div>
                                                <div class="d-flex justify-space-between py-1" v-if="currentItem.StoreVNExpDate">
                                                    <span>{{ $t('tmdt_form.date_store_vn_exp') }}:</span>
                                                    <span class="font-weight-bold">{{ currentItem.StoreVNExpDate | display_date('DD-MM-YYYY HH:mm') }}</span>
                                                </div>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>

            <v-card-actions class="pa-6 grey lighten-4 d-flex justify-end">
                <v-btn text color="grey darken-1" class="px-6" @click="goBack" :disabled="saving">
                    <v-icon left>mdi-arrow-left</v-icon>
                    {{ $t('tmdt_form.back') }}
                </v-btn>
                <v-btn color="primary" class="px-6 ml-3" @click="saveItem" :loading="saving" v-if="!roleCustomer">
                    <v-icon left>mdi-content-save</v-icon>
                    {{ $t('tmdt_form.save') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
import { mapState } from "vuex";
import configs from "@/configs";
import { sendErrorNotice, sendSuccessNotice } from "@/commons/utils";
import { display_color, display_value } from "@/commons/formater";
import _ from "lodash";

export default {
    name: "ProductDetail_TMDT",
    data() {
        return {
            saving: false,
            product_status_list: configs.PRODUCT_STATUS_LIST,
            length: 0,
            width: 0,
            height: 0,
        };
    },
    computed: {
        ...mapState({
            currentItem: (state) => state.product.selected,
            customers: (state) => state.commons.customers,
            storages: (state) => state.storage.all.data,
        }),
        roleCustomer() {
            return this.$store.state.authentication.user.roleid == configs.ROLE_TYPE.Customer;
        },
    },
    watch: {
        'currentItem.ID': {
            handler(newId, oldId) {
                if (newId !== oldId && this.currentItem) {
                    const val = this.currentItem;
                    // Load dimensions from Packages JSON
                    if (val.Packages) {
                        try {
                            const pkgs = JSON.parse(val.Packages);
                            if (pkgs && pkgs.length > 0) {
                                const pkg = pkgs[0];
                                this.length = pkg.Length || 0;
                                this.width = pkg.Width || 0;
                                this.height = pkg.Height || 0;
                            } else {
                                this.length = 0;
                                this.width = 0;
                                this.height = 0;
                            }
                        } catch (error) {
                            this.length = 0;
                            this.width = 0;
                            this.height = 0;
                        }
                    } else {
                        this.length = 0;
                        this.width = 0;
                        this.height = 0;
                    }
                }
            },
            immediate: true,
        }
    },
    methods: {
        display_color,
        getDisplayText: display_value,
        goBack() {
            this.$router.go(-1);
        },
        calcVolume() {
            const l = Number(this.length || 0);
            const w = Number(this.width || 0);
            const h = Number(this.height || 0);
            this.currentItem.Volume = (l * w * h) / 1000000;
        },
        async saveItem() {
            if (!this.$refs.edit_form.validate()) {
                return;
            }

            this.saving = true;

            try {
                // Tự động đóng gói kích thước vào JSON Packages để đồng bộ với backend
                const pkgs = [{
                    Length: Number(this.length || 0),
                    Width: Number(this.width || 0),
                    Height: Number(this.height || 0),
                    Weight: Number(this.currentItem.Weight || 0),
                    NumberOfPack: Number(this.currentItem.NumberOfPack || 1)
                }];

                // Prepare payload
                const data = {
                    ...this.currentItem,
                    GoodsType: 1, // Luôn mặc định là TN khi đi qua form này
                    Packages: JSON.stringify(pkgs),
                };

                // Save
                this.$store.dispatch("product/setDetail", {
                    data: data,
                    id: data.ID,
                }).then(ret => {
                    ret = ret.data ? ret.data : ret;
                    const productId = this.$route.params.id;
                    if (_.isEmpty(ret)) {
                        sendErrorNotice(this.$t('tmdt_form.load_failed'));
                    } else {
                        sendSuccessNotice(this.$t('tmdt_form.save_success'));
                        if (!productId || productId === "new") {
                            this.$router.push({ name: 'ProductDetail_TMDT', params: { id: ret.ID } });
                            this.load_detail();
                        } else {
                            this.load_detail();
                        }
                    }
                });
            } catch (error) {
                console.error("Save error:", error);
                sendErrorNotice(this.$t('tmdt_form.save_failed'));
            } finally {
                this.saving = false;
            }
        },
        load_detail() {
            const productId = this.$route.params.id;
            if (productId && productId !== "new") {
                this.$store.dispatch("product/getDetail", productId);
            } else {
                // Initialize default values for new TN product
                this.$store.commit("product/setDetail", { data: {
                    ID: 0,
                    UID: null,
                    StoreID: null,
                    GoodsType: 1,
                    TrackingCode: "",
                    Weight: 0,
                    Volume: 0,
                    NumberOfPack: 1,
                    Status: 1,
                    Note: "",
                    Packages: "[]",
                }});
            }
        }
    },
    created() {
        this.$root.$emit('REQUEST_ROOT_OPTION', {
            invisibleSaveButton: this.roleCustomer
        });
        this.$store.dispatch("commons/getAllCustomer");
        this.$store.dispatch("storage/getList");
        this.load_detail();
    }
};
</script>

<style scoped>
.gap-2 {
    gap: 8px;
}
</style>
